import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import LlmsAdmin from "@/components/admin/LlmsAdmin";
import { useToast } from "@/hooks/use-toast";
import { Trash2, Plus, LogOut, Edit, Building2, Rocket, Users, Newspaper, Bot, Waves } from "lucide-react";
import type { Listing } from "@/lib/listings";
import ImageUploader from "@/components/admin/ImageUploader";
import LaunchesAdmin from "@/components/admin/LaunchesAdmin";
import CrmShell from "@/components/admin/crm/CrmShell";
import BlogAdmin from "@/components/admin/BlogAdmin";
import LagoaLandingAdmin from "@/components/admin/LagoaLandingAdmin";
import LandingPagesList from "@/components/admin/LandingPagesList";
import AmenitiesSelector, { type AmenityCategory } from "@/components/admin/AmenitiesSelector";
import LandingConfigForm from "@/components/admin/LandingConfigForm";
import { EMPTY_LANDING_CONFIG, type LandingConfig } from "@/lib/landingConfig";
import RichContentForm, { EMPTY_RICH_CONTENT, type RichContentValue } from "@/components/admin/RichContentForm";
import Phase3Form, { EMPTY_PHASE3, type Phase3Value } from "@/components/admin/Phase3Form";
import Phase4Form, { EMPTY_PHASE4, type Phase4Value } from "@/components/admin/Phase4Form";
import Phase5Form, { EMPTY_PHASE5, type AiSeoConfig } from "@/components/admin/Phase5Form";
import Phase6Form, { EMPTY_PHASE6, type MarketingKit } from "@/components/admin/Phase6Form";

const NEIGHBORHOODS = [
  "Barra da Tijuca", "Recreio dos Bandeirantes", "Copacabana", "Ipanema",
  "Leblon", "Lagoa", "Botafogo", "Flamengo", "Laranjeiras", "Tijuca", "Alto da Boa Vista",
  "Centro do Rio", "Glória", "Jardim Botânico", "Gávea",
  "Anchieta", "Rio Comprido", "Maracanã",
];

const AMENITY_KEYS: AmenityCategory[] = [
  "condominium_amenities", "unit_amenities", "wellness_features",
  "furniture_features", "accessibility_features", "appliance_features", "room_features",
];

const emptyAmenities = (): Record<AmenityCategory, string[]> => ({
  condominium_amenities: [], unit_amenities: [], wellness_features: [],
  furniture_features: [], accessibility_features: [], appliance_features: [], room_features: [],
});

const AdminPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [listings, setListings] = useState<Listing[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [sendingReset, setSendingReset] = useState(false);

  const [form, setForm] = useState({
    title: "", description: "", listing_code: "",
    property_type: "apartamento" as "apartamento" | "cobertura" | "casa",
    neighborhood: "", address: "", bedrooms: 2, suites: 1, bathrooms: 1,
    garage_spots: 1, area_m2: 80, price: 500000, condominium_fee: 0, iptu: 0,
    floor: "", features: "", images: [] as string[],
    is_featured: false, is_active: true, show_in_landing_lagoa: false, show_in_landing_ipanema: false,
    condominium_images: [] as string[], condominium_description: "",
    broker_name: "", broker_phone: "", broker_photo: "", broker_creci: "",
    is_furnished: "" as "" | "true" | "false",
    near_metro: "" as "" | "true" | "false",
    video_url: "",
    watermark_enabled: false, watermark_logo: "",
    similar_listing_ids: [] as string[],
    landing_config: EMPTY_LANDING_CONFIG as LandingConfig,
    // SEO overrides (per-listing)
    meta_title: "", meta_description: "", canonical_url: "", robots: "",
    og_title: "", og_description: "", og_image: "",
    keywords_primary: "" as string, keywords_secondary: "" as string,
    schema_type: "",
    // Fase 2 — rich content, ratings, room-gallery, media
    rich_content: EMPTY_RICH_CONTENT as RichContentValue,
    // Fase 3 — lifestyle, faq, comparativo
    phase3: EMPTY_PHASE3 as Phase3Value,
    phase4: EMPTY_PHASE4 as Phase4Value,
    ai_seo: EMPTY_PHASE5 as AiSeoConfig,
    phase6: EMPTY_PHASE6 as { marketing_kit: MarketingKit; syndication_enabled: boolean },
  });

  const [amenities, setAmenities] = useState<Record<AmenityCategory, string[]>>(emptyAmenities());

  useEffect(() => { checkAuth(); }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      setIsAuthenticated(true);
      const { data: roles } = await supabase.from("user_roles").select("role").eq("user_id", session.user.id);
      const hasAdmin = roles?.some((r) => r.role === "admin") || false;
      setIsAdmin(hasAdmin);
      if (hasAdmin) loadListings();
    }
    setLoading(false);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      toast({ title: "Erro ao fazer login", description: error.message, variant: "destructive" });
    } else { checkAuth(); }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setSendingReset(true);
    const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setSendingReset(false);
    if (error) {
      toast({ title: "Erro", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "E-mail enviado!", description: "Verifique sua caixa de entrada para redefinir a senha." });
      setShowForgotPassword(false);
      setForgotEmail("");
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setIsAuthenticated(false);
    setIsAdmin(false);
    navigate("/admin");
  };

  const loadListings = async () => {
    const { data } = await supabase.from("listings").select("*").order("created_at", { ascending: false });
    if (data) setListings(data);
  };

  const resetForm = () => {
    setForm({
      title: "", description: "", listing_code: "", property_type: "apartamento", neighborhood: "",
      address: "", bedrooms: 2, suites: 1, bathrooms: 1, garage_spots: 1,
      area_m2: 80, price: 500000, condominium_fee: 0, iptu: 0, floor: "",
      features: "", images: [] as string[], is_featured: false, is_active: true, show_in_landing_lagoa: false, show_in_landing_ipanema: false,
      condominium_images: [] as string[], condominium_description: "",
      broker_name: "", broker_phone: "", broker_photo: "", broker_creci: "",
      is_furnished: "", near_metro: "", video_url: "",
      watermark_enabled: false, watermark_logo: "", similar_listing_ids: [],
      landing_config: EMPTY_LANDING_CONFIG,
      meta_title: "", meta_description: "", canonical_url: "", robots: "",
      og_title: "", og_description: "", og_image: "",
      keywords_primary: "", keywords_secondary: "", schema_type: "",
      rich_content: EMPTY_RICH_CONTENT,
      phase3: EMPTY_PHASE3,
      phase4: EMPTY_PHASE4,
      ai_seo: EMPTY_PHASE5,
      phase6: EMPTY_PHASE6,
    });
    setAmenities(emptyAmenities());
    setEditingId(null);
  };

  const handleEdit = (listing: Listing) => {
    const l = listing as any;
    setEditingId(listing.id);
    setForm({
      title: listing.title, description: listing.description || "",
      listing_code: l.listing_code || "",
      property_type: listing.property_type, neighborhood: listing.neighborhood,
      address: listing.address || "", bedrooms: listing.bedrooms,
      suites: listing.suites || 0, bathrooms: listing.bathrooms || 1,
      garage_spots: listing.garage_spots || 0, area_m2: listing.area_m2,
      price: listing.price, condominium_fee: listing.condominium_fee || 0,
      iptu: listing.iptu || 0, floor: listing.floor || "",
      features: listing.features?.join(", ") || "", images: listing.images || [],
      is_featured: listing.is_featured || false, is_active: listing.is_active !== false, show_in_landing_lagoa: l.show_in_landing_lagoa || false, show_in_landing_ipanema: l.show_in_landing_ipanema || false,
      condominium_images: l.condominium_images || [],
      condominium_description: l.condominium_description || "",
      broker_name: l.broker_name || "", broker_phone: l.broker_phone || "",
      broker_photo: l.broker_photo || "", broker_creci: l.broker_creci || "",
      is_furnished: l.is_furnished === true ? "true" : l.is_furnished === false ? "false" : "",
      near_metro: l.near_metro === true ? "true" : l.near_metro === false ? "false" : "",
      video_url: l.video_url || "",
      watermark_enabled: l.watermark_enabled || false,
      watermark_logo: l.watermark_logo || "",
      similar_listing_ids: l.similar_listing_ids || [],
      landing_config: (l.landing_config as LandingConfig) || EMPTY_LANDING_CONFIG,
      meta_title: l.meta_title || "",
      meta_description: l.meta_description || "",
      canonical_url: l.canonical_url || "",
      robots: l.robots || "",
      og_title: l.og_title || "",
      og_description: l.og_description || "",
      og_image: l.og_image || "",
      keywords_primary: (l.keywords_primary || []).join(", "),
      keywords_secondary: (l.keywords_secondary || []).join(", "),
      schema_type: l.schema_type || "",
      rich_content: {
        description_full: l.description_full || "",
        why_special: l.why_special || "",
        about_location: l.about_location || "",
        buyer_profile: l.buyer_profile || "",
        differentials: l.differentials || "",
        institutional_text: l.institutional_text || "",
        ratings: l.ratings || {},
        photo_sections: Array.isArray(l.photo_sections) ? l.photo_sections : [],
        image_hero: l.image_hero || "",
        image_card: l.image_card || "",
        image_og: l.image_og || "",
        image_thumbnail: l.image_thumbnail || "",
        video_url: l.video_url || "",
        drone_url: l.drone_url || "",
        virtual_tour_url: l.virtual_tour_url || "",
        floorplan_url: l.floorplan_url || "",
        pdf_url: l.pdf_url || "",
      },
      phase3: {
        lifestyle: Array.isArray(l.lifestyle) ? l.lifestyle : [],
        faqs: Array.isArray(l.faqs) ? l.faqs : [],
        comparison_config: l.comparison_config || EMPTY_PHASE3.comparison_config,
      },
      phase4: {
        lead_magnet: l.lead_magnet || EMPTY_PHASE4.lead_magnet,
        google_ads: l.google_ads || EMPTY_PHASE4.google_ads,
        consultant_mode: l.consultant_mode || EMPTY_PHASE4.consultant_mode,
      },
      ai_seo: (l.ai_seo as AiSeoConfig) || EMPTY_PHASE5,
      phase6: {
        marketing_kit: (l.marketing_kit as MarketingKit) || {},
        syndication_enabled: l.syndication_enabled || false,
      },
    });
    setAmenities({
      condominium_amenities: l.condominium_amenities || [],
      unit_amenities: l.unit_amenities || [],
      wellness_features: l.wellness_features || [],
      furniture_features: l.furniture_features || [],
      accessibility_features: l.accessibility_features || [],
      appliance_features: l.appliance_features || [],
      room_features: l.room_features || [],
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload: any = {
      ...form,
      listing_code: form.listing_code || null,
      features: form.features ? form.features.split(",").map((s) => s.trim()) : [],
      images: form.images,
      condominium_images: form.condominium_images,
      condominium_description: form.condominium_description || null,
      broker_name: form.broker_name || null,
      broker_phone: form.broker_phone || null,
      broker_photo: form.broker_photo || null,
      broker_creci: form.broker_creci || null,
      is_furnished: form.is_furnished === "true" ? true : form.is_furnished === "false" ? false : null,
      near_metro: form.near_metro === "true" ? true : form.near_metro === "false" ? false : null,
      video_url: form.video_url || null,
      watermark_enabled: form.watermark_enabled,
      show_in_landing_lagoa: form.show_in_landing_lagoa,
      show_in_landing_ipanema: form.show_in_landing_ipanema,
      watermark_logo: form.watermark_logo || null,
      similar_listing_ids: form.similar_listing_ids,
      landing_config: form.landing_config,
      meta_title: form.meta_title || null,
      meta_description: form.meta_description || null,
      canonical_url: form.canonical_url || null,
      robots: form.robots || null,
      og_title: form.og_title || null,
      og_description: form.og_description || null,
      og_image: form.og_image || null,
      keywords_primary: form.keywords_primary
        ? form.keywords_primary.split(",").map((s) => s.trim()).filter(Boolean)
        : [],
      keywords_secondary: form.keywords_secondary
        ? form.keywords_secondary.split(",").map((s) => s.trim()).filter(Boolean)
        : [],
      schema_type: form.schema_type || null,
      ...amenities,
      // Fase 2 — spread rich content fields (each maps to its own column)
      description_full: form.rich_content.description_full || null,
      why_special: form.rich_content.why_special || null,
      about_location: form.rich_content.about_location || null,
      buyer_profile: form.rich_content.buyer_profile || null,
      differentials: form.rich_content.differentials || null,
      institutional_text: form.rich_content.institutional_text || null,
      ratings: form.rich_content.ratings && Object.keys(form.rich_content.ratings).length ? form.rich_content.ratings : null,
      photo_sections: form.rich_content.photo_sections?.length ? form.rich_content.photo_sections : null,
      image_hero: form.rich_content.image_hero || null,
      image_card: form.rich_content.image_card || null,
      image_og: form.rich_content.image_og || null,
      image_thumbnail: form.rich_content.image_thumbnail || null,
      drone_url: form.rich_content.drone_url || null,
      virtual_tour_url: form.rich_content.virtual_tour_url || null,
      floorplan_url: form.rich_content.floorplan_url || null,
      pdf_url: form.rich_content.pdf_url || null,
      // Fase 3 — lifestyle, FAQ, comparativo
      lifestyle: form.phase3.lifestyle?.length ? form.phase3.lifestyle : null,
      faqs: form.phase3.faqs?.length ? form.phase3.faqs.filter((f) => f.question || f.answer) : null,
      comparison_config: form.phase3.comparison_config?.enabled ? form.phase3.comparison_config : null,
      // Fase 4 — Lead Magnet, Google Ads, Modo Consultor
      lead_magnet: form.phase4.lead_magnet?.enabled ? form.phase4.lead_magnet : null,
      google_ads: (form.phase4.google_ads?.headline1 || form.phase4.google_ads?.description1)
        ? form.phase4.google_ads : null,
      consultant_mode: form.phase4.consultant_mode?.enabled ? form.phase4.consultant_mode : null,
      // Fase 5 — IA & SEO Semântico
      ai_seo: form.ai_seo?.enabled ? form.ai_seo : null,
      // Fase 6 — Marketing Kit e Feed XML
      marketing_kit: form.phase6.marketing_kit && Object.keys(form.phase6.marketing_kit).length ? form.phase6.marketing_kit : null,
      syndication_enabled: !!form.phase6.syndication_enabled,
    };
    // Strip form-only nested objects that aren't columns
    delete payload.rich_content;
    delete payload.phase3;
    delete payload.phase4;
    delete payload.phase6;
    let error;
    if (editingId) {
      ({ error } = await supabase.from("listings").update(payload).eq("id", editingId));
    } else {
      ({ error } = await supabase.from("listings").insert(payload));
    }
    if (error) {
      toast({ title: "Erro", description: error.message, variant: "destructive" });
    } else {
      toast({ title: editingId ? "Imóvel atualizado!" : "Imóvel cadastrado!" });
      resetForm();
      loadListings();
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Tem certeza que deseja excluir este imóvel?")) return;
    const { error } = await supabase.from("listings").delete().eq("id", id);
    if (error) {
      toast({ title: "Erro ao excluir", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Imóvel excluído" });
      loadListings();
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen font-body">Carregando...</div>;

  if (!isAuthenticated) {
    if (showForgotPassword) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-background">
          <form onSubmit={handleForgotPassword} className="bg-card p-8 rounded-lg shadow-xl max-w-sm w-full space-y-4 border border-border">
            <h1 className="font-display text-2xl font-bold text-foreground text-center">Recuperar Senha</h1>
            <p className="font-body text-sm text-muted-foreground text-center">Digite seu e-mail para receber um link de recuperação.</p>
            <Input type="email" placeholder="E-mail" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required className="font-body" />
            <Button type="submit" variant="gold" className="w-full" disabled={sendingReset}>
              {sendingReset ? "Enviando..." : "Enviar link de recuperação"}
            </Button>
            <button type="button" onClick={() => setShowForgotPassword(false)} className="w-full text-sm text-muted-foreground hover:text-foreground font-body transition-colors">
              Voltar ao login
            </button>
          </form>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <form onSubmit={handleLogin} className="bg-card p-8 rounded-lg shadow-xl max-w-sm w-full space-y-4 border border-border">
          <h1 className="font-display text-2xl font-bold text-foreground text-center">Admin Login</h1>
          <Input type="email" placeholder="E-mail" value={email} onChange={(e) => setEmail(e.target.value)} required className="font-body" />
          <Input type="password" placeholder="Senha" value={password} onChange={(e) => setPassword(e.target.value)} required className="font-body" />
          <div className="flex justify-end">
            <button type="button" onClick={() => setShowForgotPassword(true)} className="text-xs text-muted-foreground hover:text-foreground font-body transition-colors">
              Esqueceu a senha?
            </button>
          </div>
          <Button type="submit" variant="gold" className="w-full">Entrar</Button>
          <div className="relative my-2">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
            <div className="relative flex justify-center text-xs uppercase"><span className="bg-card px-2 text-muted-foreground">ou</span></div>
          </div>
          <Button
            type="button"
            variant="outline"
            className="w-full gap-2"
            onClick={async () => {
              const result = await lovable.auth.signInWithOAuth("google", {
                redirect_uri: window.location.origin,
              });
              if (result.error) {
                toast({ title: "Erro ao fazer login com Google", description: String(result.error), variant: "destructive" });
                return;
              }
              if (result.redirected) return;
              checkAuth();
            }}
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Entrar com Google
          </Button>
        </form>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background gap-4">
        <p className="font-body text-foreground">Você não tem permissão de administrador.</p>
        <Button onClick={handleLogout} variant="outline"><LogOut className="h-4 w-4" /> Sair</Button>
      </div>
    );
  }

  const formatPrice = (p: number) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 }).format(p);

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-primary p-4 flex items-center justify-between">
        <h1 className="font-display text-xl font-bold text-primary-foreground">Topclass Admin</h1>
        <div className="flex gap-2">
          <Button variant="heroOutline" size="sm" asChild><a href="/">Ver Site</a></Button>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-primary-foreground"><LogOut className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <Tabs defaultValue="listings" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="listings" className="gap-2">
              <Building2 className="h-4 w-4" /> Imóveis
            </TabsTrigger>
            <TabsTrigger value="launches" className="gap-2">
              <Rocket className="h-4 w-4" /> Lançamentos
            </TabsTrigger>
            <TabsTrigger value="crm" className="gap-2">
              <Users className="h-4 w-4" /> CRM
            </TabsTrigger>
            <TabsTrigger value="blog" className="gap-2">
              <Newspaper className="h-4 w-4" /> Blog
            </TabsTrigger>
            <TabsTrigger value="llms" className="gap-2">
              <Bot className="h-4 w-4" /> IA / LLMs
            </TabsTrigger>
            <TabsTrigger value="lagoa" className="gap-2">
              <Waves className="h-4 w-4" /> Landing Geral
            </TabsTrigger>
          </TabsList>

          <TabsContent value="listings">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Listings Form */}
              <div className="lg:col-span-1">
                <form onSubmit={handleSubmit} className="bg-card rounded-lg p-6 shadow-lg border border-border space-y-3">
                  <h2 className="font-display text-lg font-bold text-foreground mb-2">
                    {editingId ? "Editar Imóvel" : "Novo Imóvel"}
                  </h2>
                  <Input placeholder="Código do Imóvel" value={form.listing_code} onChange={(e) => setForm({ ...form, listing_code: e.target.value })} maxLength={30} className="font-body" />
                  <Input placeholder="Título *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required maxLength={200} className="font-body" />
                  <Textarea placeholder="Descrição" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} maxLength={2000} className="font-body" />
                  <Select value={form.property_type} onValueChange={(v) => setForm({ ...form, property_type: v as any })}>
                    <SelectTrigger className="font-body"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="apartamento">Apartamento</SelectItem>
                      <SelectItem value="cobertura">Cobertura</SelectItem>
                      <SelectItem value="casa">Casa</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={form.neighborhood} onValueChange={(v) => setForm({ ...form, neighborhood: v })}>
                    <SelectTrigger className="font-body"><SelectValue placeholder="Bairro *" /></SelectTrigger>
                    <SelectContent>
                      {NEIGHBORHOODS.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input placeholder="Endereço" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} maxLength={300} className="font-body" />
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-sm font-medium text-foreground font-body block mb-1">Quartos</label>
                      <Input type="number" placeholder="Quartos" value={form.bedrooms} onChange={(e) => setForm({ ...form, bedrooms: Number(e.target.value) })} min={0} className="font-body" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground font-body block mb-1">Suítes</label>
                      <Input type="number" placeholder="Suítes" value={form.suites} onChange={(e) => setForm({ ...form, suites: Number(e.target.value) })} min={0} className="font-body" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground font-body block mb-1">Banheiros</label>
                      <Input type="number" placeholder="Banheiros" value={form.bathrooms} onChange={(e) => setForm({ ...form, bathrooms: Number(e.target.value) })} min={0} className="font-body" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground font-body block mb-1">Vagas de Garagem</label>
                      <Input type="number" placeholder="Vagas" value={form.garage_spots} onChange={(e) => setForm({ ...form, garage_spots: Number(e.target.value) })} min={0} className="font-body" />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground font-body block mb-1">Área (m²) *</label>
                    <Input type="number" placeholder="Área (m²)" value={form.area_m2} onChange={(e) => setForm({ ...form, area_m2: Number(e.target.value) })} min={1} required className="font-body" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground font-body block mb-1">Preço (R$) *</label>
                    <Input type="number" placeholder="Preço (R$)" value={form.price} onChange={(e) => setForm({ ...form, price: Number(e.target.value) })} min={1} required className="font-body" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-sm font-medium text-foreground font-body block mb-1">Condomínio (R$)</label>
                      <Input type="number" placeholder="Condomínio" value={form.condominium_fee} onChange={(e) => setForm({ ...form, condominium_fee: Number(e.target.value) })} min={0} className="font-body" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground font-body block mb-1">IPTU Anual (R$)</label>
                      <Input type="number" placeholder="IPTU anual" value={form.iptu} onChange={(e) => setForm({ ...form, iptu: Number(e.target.value) })} min={0} className="font-body" />
                    </div>
                  </div>
                  <Input placeholder="Andar" value={form.floor} onChange={(e) => setForm({ ...form, floor: e.target.value })} maxLength={20} className="font-body" />
                  <Input placeholder="Características (separadas por vírgula)" value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} className="font-body" />

                  {/* Mobiliado & Próximo ao metrô */}
                  <div className="border-t border-border pt-3 mt-3">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-sm font-medium text-foreground font-body block mb-1">Mobiliado</label>
                        <Select value={form.is_furnished} onValueChange={(v) => setForm({ ...form, is_furnished: v as any })}>
                          <SelectTrigger className="font-body"><SelectValue placeholder="Tanto faz" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="true">Sim</SelectItem>
                            <SelectItem value="false">Não</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-foreground font-body block mb-1">Próximo ao metrô</label>
                        <Select value={form.near_metro} onValueChange={(v) => setForm({ ...form, near_metro: v as any })}>
                          <SelectTrigger className="font-body"><SelectValue placeholder="Tanto faz" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="true">Sim</SelectItem>
                            <SelectItem value="false">Não</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Video */}
                  <div className="border-t border-border pt-3 mt-3">
                    <label className="text-sm font-medium text-foreground font-body block mb-1">Vídeo (URL do YouTube ou link de upload)</label>
                    <Input placeholder="https://youtube.com/watch?v=... ou URL do vídeo" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} className="font-body" />
                  </div>

                  {/* Amenities */}
                  <div className="border-t border-border pt-3 mt-3">
                    <label className="text-sm font-medium text-foreground font-body block mb-2">Características detalhadas</label>
                    <AmenitiesSelector
                      values={amenities}
                      onChange={(cat, selected) => setAmenities({ ...amenities, [cat]: selected })}
                    />
                  </div>

                  <ImageUploader images={form.images} onChange={(imgs) => setForm({ ...form, images: imgs })} />

                  <div className="border-t border-border pt-3 mt-3">
                    <label className="text-sm font-medium text-foreground font-body">Fotos do Condomínio</label>
                    <ImageUploader images={form.condominium_images} onChange={(imgs) => setForm({ ...form, condominium_images: imgs })} />
                  </div>
                  <Textarea placeholder="Descrição do Condomínio" value={form.condominium_description} onChange={(e) => setForm({ ...form, condominium_description: e.target.value })} rows={3} maxLength={2000} className="font-body" />

                  <div className="border-t border-border pt-3 mt-3">
                    <label className="text-sm font-medium text-foreground font-body">Corretor Responsável</label>
                    <div className="space-y-2 mt-1.5">
                      <div className="grid grid-cols-2 gap-2">
                        <Input placeholder="Nome do Corretor" value={form.broker_name} onChange={(e) => setForm({ ...form, broker_name: e.target.value })} maxLength={100} className="font-body" />
                        <Input placeholder="Telefone do Corretor" value={form.broker_phone} onChange={(e) => setForm({ ...form, broker_phone: e.target.value })} maxLength={20} className="font-body" />
                      </div>
                      <Input placeholder="CRECI" value={form.broker_creci} onChange={(e) => setForm({ ...form, broker_creci: e.target.value })} maxLength={30} className="font-body" />
                      <div>
                        <label className="text-sm font-medium text-foreground font-body block mb-1">Foto do Corretor</label>
                        <ImageUploader images={form.broker_photo ? [form.broker_photo] : []} onChange={(imgs) => setForm({ ...form, broker_photo: imgs[0] || "" })} />
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-border pt-3 mt-3 space-y-2">
                    <label className="text-sm font-medium text-foreground font-body">Marca d'água</label>
                    <label className="flex items-center gap-2 font-body text-sm">
                      <input
                        type="checkbox"
                        checked={form.watermark_enabled}
                        onChange={(e) => setForm({ ...form, watermark_enabled: e.target.checked })}
                      />
                      Aplicar marca d'água nas fotos do imóvel
                    </label>
                    <div>
                      <label className="text-sm font-medium text-foreground font-body block mb-1">Logomarca (para marca d'água)</label>
                      <ImageUploader
                        images={form.watermark_logo ? [form.watermark_logo] : []}
                        onChange={(imgs) => setForm({ ...form, watermark_logo: imgs[0] || "" })}
                      />
                    </div>
                  </div>

                  <div className="border-t border-border pt-3 mt-3">
                    <label className="text-sm font-medium text-foreground font-body block mb-2">
                      Imóveis Semelhantes ({form.similar_listing_ids.length} selecionados)
                    </label>
                    <p className="text-xs text-muted-foreground font-body mb-2">
                      Selecione manualmente os imóveis que aparecerão como semelhantes no final da página.
                    </p>
                    <div className="max-h-48 overflow-y-auto border border-border rounded-md p-2 space-y-1">
                      {listings.filter((l) => l.id !== editingId).map((l) => (
                        <label key={l.id} className="flex items-center gap-2 font-body text-xs p-1 hover:bg-muted rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={form.similar_listing_ids.includes(l.id)}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...form.similar_listing_ids, l.id]
                                : form.similar_listing_ids.filter((id) => id !== l.id);
                              setForm({ ...form, similar_listing_ids: next });
                            }}
                          />
                          <span className="flex-1 truncate">
                            {(l as any).listing_code && <strong className="text-primary mr-1">[{(l as any).listing_code}]</strong>}
                            {l.title} — {l.neighborhood}
                          </span>
                        </label>
                      ))}
                      {listings.length === 0 && (
                        <p className="text-xs text-muted-foreground font-body text-center py-2">Nenhum imóvel disponível.</p>
                      )}
                    </div>
                  </div>
                  <LandingConfigForm
                    value={form.landing_config}
                    onChange={(next) => setForm({ ...form, landing_config: next })}
                  />

                  <RichContentForm
                    value={form.rich_content}
                    onChange={(next) => setForm({ ...form, rich_content: next })}
                  />

                  <Phase3Form
                    value={form.phase3}
                    onChange={(next) => setForm({ ...form, phase3: next })}
                  />

                  <Phase4Form
                    value={form.phase4}
                    onChange={(next) => setForm({ ...form, phase4: next })}
                  />

                  <Phase5Form
                    value={form.ai_seo}
                    onChange={(next) => setForm({ ...form, ai_seo: next })}
                    listingContext={{
                      title: form.title,
                      neighborhood: form.neighborhood,
                      description: form.description,
                      bedrooms: form.bedrooms,
                      area_m2: form.area_m2,
                      price: form.price,
                      property_type: form.property_type,
                    }}
                    onAiFillMeta={(meta) =>
                      setForm((prev) => ({
                        ...prev,
                        meta_title: prev.meta_title || meta.meta_title || "",
                        meta_description: prev.meta_description || meta.meta_description || "",
                        keywords_primary: prev.keywords_primary || meta.keywords || "",
                      }))
                    }
                    onAiFillFaqs={(faqs) =>
                      setForm((prev) => {
                        const existing = prev.phase3?.faqs || [];
                        if (existing.length > 0) return prev; // não sobrescreve FAQs já cadastradas
                        return { ...prev, phase3: { ...prev.phase3, faqs } };
                      })
                    }
                  />

                  <Phase6Form
                    value={form.phase6}
                    onChange={(next) => setForm({ ...form, phase6: next })}
                    listingContext={{
                      title: form.title,
                      neighborhood: form.neighborhood,
                      description: form.description,
                      differentials: form.rich_content?.differentials,
                      bedrooms: form.bedrooms,
                      suites: form.suites,
                      garage_spots: form.garage_spots,
                      area_m2: form.area_m2,
                      price: form.price,
                      property_type: form.property_type,
                    }}
                  />




                  {/* ── SEO por imóvel ── */}
                  <details className="border border-border rounded-md">
                    <summary className="cursor-pointer select-none px-3 py-2 font-body font-semibold text-sm text-foreground">
                      🔎 SEO (opcional) — meta tags, Open Graph e palavras-chave
                    </summary>
                    <div className="p-3 space-y-3">
                      <Input
                        placeholder="Meta Title (se vazio, usa o título padrão)"
                        value={form.meta_title}
                        onChange={(e) => setForm({ ...form, meta_title: e.target.value })}
                        maxLength={70}
                        className="font-body"
                      />
                      <Textarea
                        placeholder="Meta Description (até 160 caracteres)"
                        value={form.meta_description}
                        onChange={(e) => setForm({ ...form, meta_description: e.target.value })}
                        rows={2}
                        maxLength={200}
                        className="font-body"
                      />
                      <Input
                        placeholder="Canonical URL (ex.: https://topclassimoveis.com/lagoa/apartamento-...)"
                        value={form.canonical_url}
                        onChange={(e) => setForm({ ...form, canonical_url: e.target.value })}
                        className="font-body"
                      />
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-muted-foreground font-body block mb-1">Robots</label>
                          <Select
                            value={form.robots || "default"}
                            onValueChange={(v) => setForm({ ...form, robots: v === "default" ? "" : v })}
                          >
                            <SelectTrigger className="font-body"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="default">Padrão (index, follow)</SelectItem>
                              <SelectItem value="index, follow">index, follow</SelectItem>
                              <SelectItem value="noindex, follow">noindex, follow</SelectItem>
                              <SelectItem value="noindex, nofollow">noindex, nofollow</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground font-body block mb-1">Schema.org type</label>
                          <Select
                            value={form.schema_type || "default"}
                            onValueChange={(v) => setForm({ ...form, schema_type: v === "default" ? "" : v })}
                          >
                            <SelectTrigger className="font-body"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="default">Padrão (Product)</SelectItem>
                              <SelectItem value="Product">Product</SelectItem>
                              <SelectItem value="RealEstateListing">RealEstateListing</SelectItem>
                              <SelectItem value="Residence">Residence</SelectItem>
                              <SelectItem value="Apartment">Apartment</SelectItem>
                              <SelectItem value="House">House</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="border-t border-border pt-3 space-y-3">
                        <p className="text-xs font-semibold text-foreground font-body">Open Graph (compartilhamento em redes sociais)</p>
                        <Input
                          placeholder="OG Title (se vazio, usa o Meta Title)"
                          value={form.og_title}
                          onChange={(e) => setForm({ ...form, og_title: e.target.value })}
                          maxLength={90}
                          className="font-body"
                        />
                        <Textarea
                          placeholder="OG Description"
                          value={form.og_description}
                          onChange={(e) => setForm({ ...form, og_description: e.target.value })}
                          rows={2}
                          maxLength={200}
                          className="font-body"
                        />
                        <Input
                          placeholder="OG Image (URL absoluta 1200×630)"
                          value={form.og_image}
                          onChange={(e) => setForm({ ...form, og_image: e.target.value })}
                          className="font-body"
                        />
                      </div>

                      <div className="border-t border-border pt-3 space-y-3">
                        <p className="text-xs font-semibold text-foreground font-body">Palavras-chave (separadas por vírgula)</p>
                        <Input
                          placeholder="Primárias (ex.: apartamento na Lagoa, cobertura Ipanema)"
                          value={form.keywords_primary}
                          onChange={(e) => setForm({ ...form, keywords_primary: e.target.value })}
                          className="font-body"
                        />
                        <Input
                          placeholder="Secundárias (ex.: vista para lagoa, 4 quartos, luxo)"
                          value={form.keywords_secondary}
                          onChange={(e) => setForm({ ...form, keywords_secondary: e.target.value })}
                          className="font-body"
                        />
                      </div>
                    </div>
                  </details>




                  <div className="flex gap-4 font-body text-sm">
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} />
                      Destaque
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                      Ativo
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={form.show_in_landing_lagoa} onChange={(e) => setForm({ ...form, show_in_landing_lagoa: e.target.checked })} />
                      Exibir na Landing da Lagoa
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={form.show_in_landing_ipanema} onChange={(e) => setForm({ ...form, show_in_landing_ipanema: e.target.checked })} />
                      Exibir na Landing de Ipanema
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" variant="gold" className="flex-1">
                      <Plus className="h-4 w-4" /> {editingId ? "Salvar" : "Cadastrar"}
                    </Button>
                    {editingId && <Button type="button" variant="outline" onClick={resetForm}>Cancelar</Button>}
                  </div>
                </form>
              </div>

              {/* Listings List */}
              <div className="lg:col-span-2">
                <h2 className="font-display text-lg font-bold text-foreground mb-4">
                  Imóveis Cadastrados ({listings.length})
                </h2>
                <div className="space-y-3">
                  {listings.map((l) => (
                    <div key={l.id} className="bg-card border border-border rounded-lg p-4 flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="font-body font-semibold text-foreground">
                          {(l as any).listing_code && <span className="text-primary font-bold mr-2">[{(l as any).listing_code}]</span>}
                          {l.title}
                        </h3>
                        <p className="text-sm text-muted-foreground font-body">
                          {l.neighborhood} · {l.bedrooms}q · {l.area_m2}m² · {formatPrice(l.price)}
                          {!l.is_active && " · (Inativo)"}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(l)}><Edit className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(l.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                      </div>
                    </div>
                  ))}
                  {listings.length === 0 && (
                    <p className="text-muted-foreground font-body text-center py-8">
                      Nenhum imóvel cadastrado. Use o formulário para adicionar.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="launches">
            <LaunchesAdmin />
          </TabsContent>

          <TabsContent value="crm">
            <CrmShell />
          </TabsContent>

          <TabsContent value="blog">
            <BlogAdmin />
          </TabsContent>
          <TabsContent value="llms">
            <LlmsAdmin />
          </TabsContent>


          <TabsContent value="lagoa">
            <LandingPagesList />
            <LagoaLandingAdmin />
          </TabsContent>

        </Tabs>
      </div>
    </div>
  );
};

export default AdminPage;
